import React, { useEffect, useState, useReducer } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Switch,
  List,
  ListItem,
  ListItemText,
  Divider,
  Table,
  TableBody,
  IconButton,
  Tooltip,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Card,
  CardContent,
  TableHead,
  TablePagination,
  Stack
} from "@mui/material";
import { I18n } from "aws-amplify/utils";
import { get } from "aws-amplify/api";
import { post } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "components/APIStatusDialog";
import dayjs from "dayjs";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import AccountNumberStep from "./AccountNumberStep";
import TelemonitoringAssetStep from "./TelemonitoringAssetStep";
import EmailInfoStep from "./EmailInfoStep";

const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    const audit = action.value;
    if (Array.isArray(audit)) {
      audit.forEach((element) => {
        if (typeof element.timestamp === "number") {
          element.timestamp = dayjs(element.timestamp).format(
            "YYYY-MM-DD HH:mm"
          );
        }
      });
      return { audit: audit, fetched: true };
    }
  }
  return lastState;
};

function assetReducer(state, action) {
    switch (action.type) {
      case "DATA_RECEIVED":
        return { ...state, loading: false, data: action.value, error: null };
      case "ERROR":
        return { ...state, loading: false, error: action.error };
      default:
        return state;
    }
}

const initialState = {
    loading: true,
    data: null,
    error: null,
};

function AddNewLinePopup({ isOpen, onClose, handleMapEmailSubmit }) {
    const [assetState, dispatchAssetState] = useReducer(
        assetReducer, initialState
    );
    
    const [state, dispatch] = useReducer(reducer, {
        audit: [],
        fetched: false,
    });
    const [disableBackButton, setDisableBackButton] = useState(false);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);
    const [selectedAccountAssets, setSelectedAccountAssets] = useState(null);
    const [selectedEmail, setSelectedEmail] = useState(null);

    const steps = [I18n.get("Find Account Number"), I18n.get("Select Telemonitoring Asset"), I18n.get("Map Email")];
    
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        account_number: "",
        account_id: "",
        search_pattern: "",
        email_address: "",
        tlm_asset_id: "",
        delay: "",
        rule: "",
        flag: false,
        unrec_email: false,
        word_key: "",
    });

    const validEmail = new RegExp('^[a-zA-Z0-9._:$!%-]+@[a-zA-Z0-9.-]{1,}[.][a-zA-Z]{2,}$');

    const fetchAccountNumbers = async () => {
      try {
          const restOperation = get({
          apiName: "TelemonitoringAPI",
          path: "/accountNumber/get",
          options: {
              headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
              },
          },
          });
          const { body } = await restOperation.response;
          const response = await body.json();
          dispatch({ type: "DATA_RECEIVED", value: response });
      } catch (error) {
          console.error("Error during API call:", error);
      }
    };

    const fetchAccountAssets = async (accountID) => {
      try {
        const response = get({
          apiName: "TelemonitoringAPI",
          path: "/accountNumber/asset",
          options: {
            headers: {
              "Content-Type": "application/json",
              Authorization: `${await currentUserToken()}`,
            },
            queryParams: {
              account_id: accountID,
              record_type_id: "0126N000000ChTeQAK",
            },
          },
        });
        const { body } = await response.response;
        return await body.json();
      } catch (error) {
        console.error("Error fetching alarms:", error);
        throw error;
      }
    };

  const handleNext = () => {
      setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
      setCurrentStep(currentStep - 1);
  };

  const handleMainFormData = (field, value) => {
      setFormData((currentState) => ({
        ...currentState,
        [field]: value,
      }));
  };

  const handleMapEmail = () => {
    handleMapEmailSubmit(formData);
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>{I18n.get("Map Email")}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={currentStep}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {currentStep === 0 && (
          <AccountNumberStep
            fetchData={fetchAccountNumbers}
            handleMainFormData={handleMainFormData}
            state={state}
            selectedAccountNumber={selectedAccountNumber}
            setSelectedAccountNumber={setSelectedAccountNumber}
            mainFormData={formData}
          />
        )}
        {currentStep === 1 && (
          <TelemonitoringAssetStep 
              fetchData={fetchAccountAssets}
              handleMainFormData={handleMainFormData}
              state={state}
              selectedAccountAssets={selectedAccountAssets}
              setSelectedAccountAssets={setSelectedAccountAssets}
              mainFormData={formData}
          />
        )}
        {currentStep === 2 && (
          <EmailInfoStep 
            //   fetchData={fetchAccountAssets}
              handleMainFormData={handleMainFormData}
              state={state}
              selectedAccountAssets={selectedAccountAssets}
              selectedEmail={selectedEmail}
              setSelectedAccountAssets={setSelectedAccountAssets}
              mainFormData={formData}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ pt: 2 }}>
        {currentStep > 0 && (
          <Button
            color="primary"
            disabled={disableBackButton}
            onClick={handleBack}
          >
            {I18n.get("Back")}
          </Button>
        )}
        {currentStep === 0 && (
          <>
            <Button
              type="button"
              color="primary"
              onClick={onClose}
              style={{ marginLeft: "10px" }}
            >
              {I18n.get("Cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleNext}
              disabled={!formData.account_number}
            >
              {I18n.get("Next")}
            </Button>
          </>
        )}
        {currentStep === 1 && (
          <>
            <Button
              type="button"
              color="primary"
              onClick={onClose}
              style={{ marginLeft: "10px" }}
            >
              {I18n.get("Cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleNext}
              disabled={!formData.tlm_asset_id}
            >
              {I18n.get("Next")}
            </Button>
          </>
        )}
        {currentStep === 2 && (
          <>
            <Button
              color="primary"
              onClick={onClose}
              style={{ marginLeft: "10px" }}
            >
              {I18n.get("Cancel")}
            </Button>
            <Button
              color="primary"
              onClick={handleMapEmail}
              disabled={
                !formData.tlm_asset_id ||
                formData.tlm_asset_id === null ||
                !formData.account_number ||
                formData.account_number === null ||
                !formData.delay ||
                formData.delay === null ||
                !formData.rule ||
                formData.rule === null ||
                (formData.rule === "email" && !validEmail.test(formData.email_address)) ||
                (formData.rule === "email" &&
                  (!formData.email_address || formData.email_address === null )) ||
                (formData.rule === "subject" &&
                  (!formData.word_key || formData.word_key === null))
              }
            >
              {I18n.get("Submit")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

AddNewLinePopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddNewLinePopup;
